import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchImpersonationOrgs } from "../../sideEffects";

const MAX_ORGS = 10;

const matchesOrgSearch = (org, searchTerm) => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) {
    return true;
  }
  return (
    org.name.toLowerCase().includes(query) || String(org.id).includes(query)
  );
};

function ImpersonationScopeInput({
  allOrgs,
  createAlert,
  selectedEnvironments,
  selectedOrgs,
  setAllOrgs,
  setSelectedOrgs,
}) {
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const requestVersionRef = useRef(0);
  const containerRef = useRef(null);
  const environment = selectedEnvironments[0];
  const specificScopeAvailable = selectedEnvironments.length === 1;

  const visibleOrgs = useMemo(
    () => orgs.filter((org) => matchesOrgSearch(org, search)),
    [orgs, search],
  );

  const loadOrgs = useCallback(async () => {
    if (!specificScopeAvailable) return;

    const requestVersion = ++requestVersionRef.current;
    setLoading(true);
    try {
      const fetchedOrgs = await fetchImpersonationOrgs(environment);
      if (requestVersion === requestVersionRef.current) {
        setOrgs(fetchedOrgs);
      }
    } catch (error) {
      if (requestVersion === requestVersionRef.current) {
        createAlert({
          content: error.message,
          theme: "danger",
          duration: 10000,
        });
      }
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [createAlert, environment, specificScopeAvailable]);

  useEffect(() => {
    requestVersionRef.current += 1;
    setOrgs([]);
    setSearch("");
    setMenuOpen(false);
    if (!allOrgs && specificScopeAvailable) {
      loadOrgs();
    }
  }, [allOrgs, loadOrgs, specificScopeAvailable]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpen]);

  const selectedIds = new Set(selectedOrgs.map(({ id }) => id));
  const toggleOrg = (org) => {
    if (selectedIds.has(org.id)) {
      setSelectedOrgs(selectedOrgs.filter(({ id }) => id !== org.id));
    } else if (selectedOrgs.length < MAX_ORGS) {
      setSelectedOrgs([...selectedOrgs, org]);
    }
  };

  const selectAllVisible = () => {
    const selectedById = new Map(selectedOrgs.map((org) => [org.id, org]));
    for (const org of visibleOrgs) {
      if (selectedById.size === MAX_ORGS) break;
      selectedById.set(org.id, org);
    }
    setSelectedOrgs([...selectedById.values()]);
  };

  return (
    <fieldset className="mb-4">
      <legend className="form-label">Organization scope</legend>
      <div className="form-check">
        <input
          id="specific-organizations"
          className="form-check-input"
          type="radio"
          name="organization-scope"
          checked={!allOrgs}
          disabled={!specificScopeAvailable}
          onChange={() => setAllOrgs(false)}
        />
        <label className="form-check-label" htmlFor="specific-organizations">
          Specific organizations
        </label>
      </div>
      <div className="form-check">
        <input
          id="all-organizations"
          className="form-check-input"
          type="radio"
          name="organization-scope"
          checked={allOrgs}
          onChange={() => {
            setAllOrgs(true);
            setSelectedOrgs([]);
            setMenuOpen(false);
          }}
        />
        <label className="form-check-label" htmlFor="all-organizations">
          All organizations
        </label>
      </div>
      <div
        className={`form-text ms-4 ${allOrgs ? "text-warning" : "invisible"}`}
        role={allOrgs ? "alert" : undefined}
        aria-hidden={!allOrgs}
      >
        This grants access to every organization. Use only when specific
        organization scope is insufficient.
      </div>

      {!specificScopeAvailable && (
        <div className="form-text">
          {selectedEnvironments.length > 1
            ? "Multi-environment requests require all-organizations access."
            : "Select one environment to choose specific organizations."}
        </div>
      )}

      {specificScopeAvailable && !allOrgs && (
        <div className="mt-3" ref={containerRef}>
          <label htmlFor="organization-search" className="form-label">
            Organizations
          </label>
          <div className="dropdown w-100">
            <input
              id="organization-search"
              className="form-control"
              value={search}
              maxLength="100"
              placeholder="Type to filter, or open to scroll"
              aria-label="Filter organizations"
              aria-expanded={menuOpen}
              aria-controls="organization-options"
              aria-autocomplete="list"
              role="combobox"
              autoComplete="off"
              onFocus={() => setMenuOpen(true)}
              onChange={({ target }) => {
                setSearch(target.value);
                setMenuOpen(true);
              }}
            />
            {menuOpen && (
              <div
                id="organization-options"
                className="dropdown-menu show w-100 mt-1 p-2"
                style={{ maxHeight: "14rem", overflowY: "auto" }}
                role="listbox"
                aria-label="Organization options"
              >
                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                  <span className="form-text mb-0">
                    {loading
                      ? "Loading…"
                      : `Showing ${visibleOrgs.length} of ${orgs.length}`}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link py-0"
                    disabled={
                      loading ||
                      !visibleOrgs.length ||
                      selectedOrgs.length === MAX_ORGS
                    }
                    onClick={selectAllVisible}
                  >
                    Select all visible
                  </button>
                </div>
                {!loading && !visibleOrgs.length && (
                  <div className="form-text px-1">No organizations found.</div>
                )}
                {visibleOrgs.map((org) => {
                  const checked = selectedIds.has(org.id);
                  return (
                    <div className="form-check" key={org.id}>
                      <input
                        id={`impersonation-org-${org.id}`}
                        className="form-check-input"
                        type="checkbox"
                        role="option"
                        aria-selected={checked}
                        checked={checked}
                        disabled={!checked && selectedOrgs.length === MAX_ORGS}
                        onChange={() => toggleOrg(org)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`impersonation-org-${org.id}`}
                      >
                        {org.name} ({org.id})
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-text mt-1">
            Selected {selectedOrgs.length} of {MAX_ORGS}. Open the list to
            scroll, or type to filter by name or ID.
          </div>

          {selectedOrgs.length > 0 && (
            <ul className="list-group mt-2" aria-label="Selected organizations">
              {selectedOrgs.map((org) => (
                <li
                  className="list-group-item d-flex justify-content-between align-items-center py-1"
                  key={org.id}
                >
                  <span>
                    {org.name} ({org.id})
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    aria-label={`Remove ${org.name}`}
                    onClick={() => toggleOrg(org)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedOrgs.length === 0 && (
            <div className="text-danger mt-1" role="alert">
              Select at least one organization.
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}

export default ImpersonationScopeInput;
